/**
 * DialerFAB — floating "Nouvel appel" button (bottom-right) that opens a
 * numeric keypad modal for placing outbound calls to any number, even one
 * that is not stored on a candidate. Uses the same Twilio pipeline
 * (recording + transcription + AI summary) as candidate calls; the
 * resulting CallLog has candidate_id = null.
 *
 * Only rendered for roles allowed to call (admin + pre_recruteur).
 */
import * as Dialog from "@radix-ui/react-dialog";
import { motion } from "framer-motion";
import { Delete, Phone, PhoneOutgoing, UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { useCallCenter } from "../../lib/callCenter";
import SaveContactDialog from "./SaveContactDialog";

const KEYS = [
    ["1", ""],
    ["2", "ABC"],
    ["3", "DEF"],
    ["4", "GHI"],
    ["5", "JKL"],
    ["6", "MNO"],
    ["7", "PQRS"],
    ["8", "TUV"],
    ["9", "WXYZ"],
    ["*", ""],
    ["0", "+"],
    ["#", ""],
];


function normalise(raw) {
    return raw.trim().replace(/\s+/g, "");
}

/** Loose E.164 check: starts with + or 0, digits only allowed after. */
function looksValidPhone(raw) {
    const n = normalise(raw);
    if (!n) return false;
    if (!/^\+?[\d*#]+$/.test(n)) return false;
    return n.replace(/[^\d]/g, "").length >= 8;
}


export default function DialerFAB() {
    const { canCall, startOutboundCall, activeCall, incomingCall } = useCallCenter();
    const [open, setOpen] = useState(false);
    const [number, setNumber] = useState("");
    const [saveOpen, setSaveOpen] = useState(false);

    // Keyboard input while modal open
    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (e.key === "Backspace") {
                setNumber((n) => n.slice(0, -1));
            } else if (e.key === "Enter") {
                e.preventDefault();
                submit();
            } else if (/^[\d+*#]$/.test(e.key)) {
                setNumber((n) => (n + e.key).slice(0, 20));
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const canDial = useMemo(() => looksValidPhone(number), [number]);

    // Hide the FAB entirely for roles that can't make calls
    if (!canCall) return null;
    // Hide while another call is active — the CallDock handles UX in that case
    if (activeCall || incomingCall) return null;

    const submit = async () => {
        if (!canDial) {
            toast.error("Numéro invalide — utilisez le format international (+33...)");
            return;
        }
        // Reuse the Twilio outbound pipeline. `candidate_id: null` = manual call.
        setOpen(false);
        await startOutboundCall({
            id: null,
            first_name: null,
            last_name: null,
            phone: normalise(number),
        });
        setNumber("");
    };

    const press = (digit) => setNumber((n) => (n + digit).slice(0, 20));
    const backspace = () => setNumber((n) => n.slice(0, -1));

    return (
        <Dialog.Root open={open} onOpenChange={setOpen}>
            <Dialog.Trigger asChild>
                <button
                    type="button"
                    data-testid="dialer-fab"
                    title="Composer un numéro"
                    className="fixed bottom-6 right-24 z-[55] h-14 w-14 rounded-full bg-shariti-black hover:bg-slate-800 text-shariti-yellow shadow-2xl inline-flex items-center justify-center transition hover:scale-105 border-2 border-shariti-yellow/40"
                >
                    <PhoneOutgoing className="h-5 w-5" />
                </button>
            </Dialog.Trigger>

            <Dialog.Portal>
                <Dialog.Overlay
                    data-testid="dialer-overlay"
                    className="fixed inset-0 z-[70] bg-shariti-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in"
                />
                <Dialog.Content
                    data-testid="dialer-dialog"
                    className="fixed left-1/2 top-1/2 z-[71] w-[92vw] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-3xl bg-white shadow-2xl focus:outline-none"
                >
                    <motion.div layout className="p-5">
                        <div className="flex items-center justify-between mb-3">
                            <Dialog.Title className="text-base font-extrabold text-shariti-black inline-flex items-center gap-2">
                                <PhoneOutgoing className="h-4 w-4 text-shariti-orange" />
                                Nouvel appel
                            </Dialog.Title>
                            <Dialog.Close asChild>
                                <button
                                    type="button"
                                    aria-label="Fermer"
                                    className="h-8 w-8 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>

                        {/* Number display */}
                        <div className="rounded-2xl bg-shariti-cream/60 border-2 border-shariti-orange/20 px-4 py-3 mb-4">
                            <input
                                type="text"
                                inputMode="tel"
                                value={number}
                                onChange={(e) => setNumber(e.target.value.slice(0, 20))}
                                placeholder="+33 6 12 34 56 78"
                                autoFocus
                                data-testid="dialer-input"
                                className="w-full bg-transparent border-0 focus:ring-0 focus:outline-none text-2xl font-extrabold text-shariti-black tracking-wide text-center placeholder:text-slate-400 placeholder:font-medium"
                            />
                        </div>

                        {/* Keypad */}
                        <div className="grid grid-cols-3 gap-2">
                            {KEYS.map(([digit, letters]) => (
                                <button
                                    key={digit}
                                    type="button"
                                    onClick={() => press(digit)}
                                    data-testid={`dialer-key-${digit}`}
                                    className="h-14 rounded-2xl bg-slate-50 hover:bg-shariti-cream border border-slate-200 hover:border-shariti-orange/40 transition group flex flex-col items-center justify-center"
                                >
                                    <span className="text-xl font-extrabold text-shariti-black group-hover:text-shariti-orange transition">{digit}</span>
                                    {letters && (
                                        <span className="text-[9px] font-black tracking-widest text-slate-400 mt-0.5">{letters}</span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {/* Action row */}
                        <div className="mt-4 grid grid-cols-[auto_1fr_auto] gap-2 items-center">
                            <button
                                type="button"
                                onClick={backspace}
                                data-testid="dialer-backspace"
                                className="h-14 w-14 rounded-2xl bg-slate-100 hover:bg-slate-200 text-slate-500 inline-flex items-center justify-center transition"
                                aria-label="Effacer"
                            >
                                <Delete className="h-5 w-5" />
                            </button>
                            <button
                                type="button"
                                onClick={submit}
                                disabled={!canDial}
                                data-testid="dialer-call"
                                className="h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white font-extrabold inline-flex items-center justify-center gap-2 shadow-btn transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Phone className="h-5 w-5" />
                                <span>Appeler</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => { if (!canDial) { toast.error("Composez un numéro valide d'abord"); return; } setSaveOpen(true); }}
                                data-testid="dialer-save-contact"
                                title="Enregistrer ce numéro comme candidat"
                                className="h-14 w-14 rounded-2xl bg-shariti-orange/10 hover:bg-shariti-orange/20 text-shariti-orange inline-flex items-center justify-center transition border-2 border-shariti-orange/30 disabled:opacity-40 disabled:cursor-not-allowed"
                                disabled={!canDial}
                                aria-label="Enregistrer comme candidat"
                            >
                                <UserPlus className="h-5 w-5" />
                            </button>
                        </div>

                        {/* Hint under actions */}
                        <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-slate-400 text-center">
                            💡 Vert = appeler · Orange = enregistrer le contact
                        </div>
                    </motion.div>
                </Dialog.Content>
            </Dialog.Portal>

            {/* Save contact dialog — separate from call dialer */}
            <SaveContactDialog
                open={saveOpen}
                onOpenChange={setSaveOpen}
                phone={normalise(number)}
                onSaved={(cand) => {
                    setSaveOpen(false);
                    setOpen(false);
                    setNumber("");
                    toast.success(`✅ ${cand.first_name} ${cand.last_name} enregistré · fiche créée`);
                }}
            />
        </Dialog.Root>
    );
}
